import React, { useCallback } from "react";
import { DatagridHeaderProps, useListContext, useResourceContext, DatagridClasses } from "react-admin";
import { TableHead, TableRow } from "@mui/material";
import clsx from "clsx";
import DatagridHeaderCell from "./DatagridHeaderCell";

// A custom header component for ra-Datagrid, which is a cloned lightweight (no selecting or bulk actions) instance of ra-DatagridHeader.
// It uses the custom DatagridHeaderCell component to control the rendered column headers.
// https://marmelab.com/react-admin/Datagrid.html#header
// https://marmelab.com/react-admin/ListTutorial.html#building-a-custom-sort-control
// Source: https://github.com/marmelab/react-admin/blob/master/packages/ra-ui-materialui/src/list/datagrid/DatagridHeader.tsx
export function DatagridHeader(props: DatagridHeaderProps) {
  const { children, className } = props;
  const resource = useResourceContext();
  const { sort, setSort } = useListContext();

  const updateSortCallback = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      event.stopPropagation();
      const newField = event.currentTarget.dataset.field;
      const newOrder =
        sort.field === newField ? (sort.order === "ASC" ? "DESC" : "ASC") : event.currentTarget.dataset.order;
      if (!newField || !newOrder || (newOrder !== "ASC" && newOrder !== "DESC")) return;
      setSort({ field: newField, order: newOrder });
    },
    [sort.field, sort.order, setSort],
  );

  return (
    <TableHead className={clsx(className, DatagridClasses.thead)}>
      <TableRow className={clsx(DatagridClasses.row, DatagridClasses.headerRow)}>
        {React.Children.map(children, (field, index) =>
          React.isValidElement(field) ? (
            <DatagridHeaderCell
              className={clsx(DatagridClasses.headerCell, `column-${field.props.source}`)}
              sort={sort}
              field={field}
              isSorting={sort.field === (field.props.sortBy || field.props.source)}
              key={field.props.source || index}
              resource={resource}
              updateSort={updateSortCallback}
            />
          ) : null,
        )}
      </TableRow>
    </TableHead>
  );
}
